import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from "../api/client";
import { ensureAbsoluteUrl } from "../utils/profileMedia";
import { Heart, Shield, Users, Sparkles } from 'lucide-react';


const ReferralLanding = () => {
  const { refCode: rawRefCode } = useParams();
  const navigate = useNavigate();

  // Extract the actual referral code from the slug.
  // New format: "Unity-UL-9XYP6C" → code is "UL-9XYP6C"
  // Old format: "UL-9XYP6C" → code is "UL-9XYP6C"
  // All referral codes start with "UL-", so split on that.
  const refCode = rawRefCode && rawRefCode.includes('UL-')
    ? 'UL-' + rawRefCode.split('UL-').pop()
    : rawRefCode;

  const [loading, setLoading] = useState(true);
  const [inviterName, setInviterName] = useState('');
  const [inviterAvatarUrl, setInviterAvatarUrl] = useState('');
  const [referralCode, setReferralCode] = useState(refCode || '');

  useEffect(() => {
    // Store referral code for signup attribution
    if (refCode) {
      localStorage.setItem('ref_code', refCode);
      setReferralCode(refCode);
    }

    if (!refCode) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchInviter = async () => {
      try {
        const res = await apiClient.get(`/referrals/lookup`, {
          params: { code: refCode },
        });

        if (cancelled) return;
        const data = res.data || {};

        if (data.inviter_name) {
          setInviterName(data.inviter_name);
        }
        if (data.inviter_avatar_url) {
          setInviterAvatarUrl(ensureAbsoluteUrl(data.inviter_avatar_url));
        }
      } catch (err) {
        console.warn('[ReferralLanding] Failed to fetch inviter info', err?.response?.status);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchInviter();
    return () => {
      cancelled = true;
    };
  }, [refCode]);

  const initial = inviterName?.trim()?.[0]?.toUpperCase() || '?';

  const handleStart = () => {
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fff7f9] flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading your invitation…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fff7f9] flex flex-col">
      <div className="flex-grow">
        <div className="max-w-md mx-auto px-6 py-10 sm:py-16">
          {/* Brand */}
          <header className="flex flex-col items-center mb-8">
            <div className="flex items-center space-x-2">
              <Heart className="w-7 h-7 text-rose-600 fill-current" />
              <span className="font-playfair text-2xl font-bold text-gray-900">
                Unity Love
              </span>
            </div>
          </header>

          {/* Inviter avatar */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              {/* Glow behind */}
              <div className="absolute inset-0 rounded-full bg-rose-400/25 blur-2xl -z-10" />
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-rose-100 border-4 border-white shadow-[0_12px_40px_rgba(244,114,182,0.4)] overflow-hidden flex items-center justify-center">
                {inviterAvatarUrl ? (
                  <img
                    src={inviterAvatarUrl}
                    alt={inviterName || 'Inviter'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl sm:text-4xl font-bold text-rose-600">
                    {initial}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Label */}
          <p className="text-xs font-semibold tracking-[0.18em] text-rose-500 text-center uppercase mb-2">
            You were personally invited
          </p>

          {/* Headline */}
          <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-gray-900 leading-tight text-center mb-4">
            <span className="text-rose-700">
              {inviterName || 'Your friend'}
            </span>{' '}
            invited you
            <br />
            to Unity Love
          </h1>

          {/* Value prop */}
          <p className="text-base sm:text-lg text-gray-700 text-center mb-8">
            Join a growing community focused on real connections in a space that
            celebrates all forms of love — monogamous, polyamorous, and
            everything in between.
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleStart}
              className="btn-primary w-full sm:w-auto text-base sm:text-lg px-8 py-3"
            >
              Start Your Journey
            </button>
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="text-rose-600 font-semibold hover:text-rose-700"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Trust / pillars section */}
          <section className="mt-12 border-t border-rose-100 pt-10">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">All Forms of Love</h3>
                  <p className="text-sm text-gray-600">
                    Whether you&apos;re monogamous, polyamorous, or building a poly
                    family, you&apos;re welcome here.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Safe &amp; Verified</h3>
                  <p className="text-sm text-gray-600">
                    Age checks, profile moderation, and community guidelines keep
                    everyone safe.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Vibrant Community</h3>
                  <p className="text-sm text-gray-600">
                    Connect in the Lifestyle Lounge, join events, and build real
                    relationships.
                  </p>
                </div>
              </div>
            </div>

            {referralCode && (
              <p className="mt-10 text-center text-xs text-gray-400">
                Referral Code:{' '}
                <span className="font-mono tracking-wide">
                  {referralCode}
                </span>
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReferralLanding;
